import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

declare let gtag: (...args: any[]) => void;

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private scrollDepths = new Set<number>();
  private pageStartTime: number;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {}

  public initGoogleAnalytics(measurementId: string): void {
    if ((window as any).gtag) {
      return;
    }

    // Add the Google Analytics script
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    this.document.head.appendChild(script);

    // Add the gtag configuration
    const configScript = this.document.createElement('script');
    configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', {
        page_path: window.location.pathname,
        send_page_view: false
      });
    `;
    this.document.head.appendChild(configScript);

    // Set up automatic page view tracking
    this.setupPageViewTracking();
    
    // Set up scroll tracking
    this.setupScrollTracking();
    
    // Set up error tracking
    this.setupErrorTracking();
    
    // Set up performance tracking
    this.setupPerformanceTracking();
    
    // Track initial page view
    setTimeout(() => {
      this.trackPageView(window.location.pathname);
    }, 100);
  }

  private setupPageViewTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Track page timing for previous page
        if (this.pageStartTime) {
          const timeOnPage = Date.now() - this.pageStartTime;
          this.trackTiming('page_view', 'time_on_page', timeOnPage);
        }
        
        // Reset scroll tracking for new page
        this.scrollDepths.clear();
        this.pageStartTime = Date.now();
        
        // Track new page view
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  private setupScrollTracking(): void {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkScrollDepth();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  private checkScrollDepth(): void {
    const windowHeight = window.innerHeight;
    const documentHeight = this.document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || this.document.documentElement.scrollTop;
    const scrollPercentage = Math.round((scrollTop + windowHeight) / documentHeight * 100);

    // Track 25%, 50%, 75%, and 100% scroll depths
    [25, 50, 75, 100].forEach(depth => {
      if (scrollPercentage >= depth && !this.scrollDepths.has(depth)) {
        this.scrollDepths.add(depth);
        this.trackEvent('scroll', {
          event_category: 'engagement',
          event_label: `${depth}%`,
          value: depth
        });
      }
    });
  }

  private setupErrorTracking(): void {
    window.addEventListener('error', (event) => {
      this.trackEvent('exception', {
        description: `${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`,
        fatal: false
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.trackEvent('exception', {
        description: `Unhandled Promise rejection: ${event.reason}`,
        fatal: false
      });
    });
  }

  private setupPerformanceTracking(): void {
    if ('performance' in window && 'timing' in window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const timing = window.performance.timing;
          const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
          const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;
          const pageRenderTime = timing.domComplete - timing.domLoading;

          this.trackTiming('page_performance', 'page_load', pageLoadTime);
          this.trackTiming('page_performance', 'dom_ready', domReadyTime);
          this.trackTiming('page_performance', 'page_render', pageRenderTime);
        }, 0);
      });
    }
  }

  public trackPageView(url: string): void {
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', {
        page_path: url,
        page_title: this.document.title,
        page_location: window.location.href
      });
    }
  }

  public trackEvent(
    eventName: string,
    eventParams?: {
      event_category?: string;
      event_label?: string;
      value?: number;
      [key: string]: any;
    }
  ): void {
    if (typeof gtag === 'function') {
      gtag('event', eventName, eventParams);
    }
  }

  public trackTiming(
    timingCategory: string,
    timingVariable: string,
    timingValue: number,
    timingLabel?: string
  ): void {
    if (typeof gtag === 'function') {
      gtag('event', 'timing_complete', {
        name: timingVariable,
        value: Math.round(timingValue),
        event_category: timingCategory,
        event_label: timingLabel
      });
    }
  }

  public trackSearch(searchTerm: string, searchCategory?: string): void {
    if (typeof gtag === 'function') {
      gtag('event', 'search', {
        search_term: searchTerm,
        search_category: searchCategory
      });
    }
  }

  public trackOutboundLink(url: string): void {
    if (typeof gtag === 'function') {
      gtag('event', 'click', {
        event_category: 'outbound',
        event_label: url,
        transport_type: 'beacon',
        event_callback: () => {
          this.document.location.href = url;
        }
      });
    }
  }

  public setUserProperties(properties: { [key: string]: any }): void {
    if (typeof gtag === 'function') {
      gtag('set', { user_properties: properties });
    }
  }

  public setUserId(userId: string): void {
    if (typeof gtag === 'function') {
      gtag('config', (window as any).GA_MEASUREMENT_ID, {
        user_id: userId
      });
    }
  }
}